import {
  Alert,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabProvider,
} from "@gravity-ui/uikit";
import { ExportFormats, TransformersEnum } from "definitions/exporter";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  HTML_TAG_REGEX,
  htmlTemplateToPlain,
} from "src/components/RichTemplateEditor";

import type { ExportTaskFormValues } from "../../ExportTasksForm.types";
import { TemplateEditor, type TemplateField } from "./TemplateEditor";
import { PREVIEW_SAMPLES, toPreviewProduct } from "./TemplatesForm.constants";
import css from "./TemplatesForm.module.scss";
import { useTemplatePreview } from "./useTemplatePreview";
import { useTemplateVariables } from "./useTemplateVariables";
import { VariableSidebar } from "./VariableSidebar";

const EMPTY_TRANSFORMERS: TransformersEnum[] = [];

const TAB_LABELS: Record<TemplateField, string> = {
  title: "Название",
  description: "Описание",
};

const isTemplateField = (value: string): value is TemplateField =>
  value === "title" || value === "description";

export const TemplatesForm = () => {
  const { control, getValues, setValue } =
    useFormContext<ExportTaskFormValues>();
  // useWatch вместо watch() — изолирует подписку на уровне хука, не дёргает
  // TemplatesForm на каждое изменение в чужих полях формы экспорта.
  const titleTemplate = useWatch({ control, name: "titleTemplate" });
  const descriptionTemplate = useWatch({
    control,
    name: "descriptionTemplate",
  });
  const transformersWatched = useWatch({ control, name: "transformers" });
  const transformers = transformersWatched ?? EMPTY_TRANSFORMERS;
  // Description у Avito поддерживает ограниченный HTML — даём WYSIWYG.
  // Title всегда plain-text (avito валидирует строго), остальные форматы
  // тоже используем textarea-чип-редактор: HTML им не нужен.
  const format = useWatch({ control, name: "format" });
  const richDescription = format === ExportFormats.Avito;

  // При смене формата с Avito на не-Avito конвертируем уже-сохранённый
  // HTML-шаблон обратно в plain-text: иначе литералы `<p>`, `<br>`, `<li>`
  // уйдут сырыми в CSV/XML/XLSX, у которых нет Avito-санитизатора. Срабатывает
  // строго на переходе rich → plain, чтобы не трогать поле в стационарных
  // состояниях и не делать форму dirty без причины.
  const wasRichRef = useRef(richDescription);
  useEffect(() => {
    const wasRich = wasRichRef.current;
    wasRichRef.current = richDescription;
    if (!wasRich || richDescription) return;
    const current = getValues("descriptionTemplate.template");
    if (!current || !HTML_TAG_REGEX.test(current)) return;
    setValue("descriptionTemplate.template", htmlTemplateToPlain(current), {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [richDescription, getValues, setValue]);

  const [activeTab, setActiveTab] = useState<TemplateField>("title");

  // Если у активного редактора (textarea-чип или TipTap) есть insertAtCursor —
  // он сюда подпишется через exposeInsert. Так общий сайдбар вставляет
  // переменную ровно в курсор того редактора, на котором юзер сейчас.
  const insertFnRef = useRef<(placeholder: string) => void>(() => {});

  const exposeInsertForTitle = useCallback(
    (fn: (p: string) => void) => {
      if (activeTab === "title") insertFnRef.current = fn;
    },
    [activeTab],
  );
  const exposeInsertForDescription = useCallback(
    (fn: (p: string) => void) => {
      if (activeTab === "description") insertFnRef.current = fn;
    },
    [activeTab],
  );

  const handleSidebarInsert = useCallback((placeholder: string) => {
    insertFnRef.current(placeholder);
  }, []);

  // Шаблон активной вкладки — включён, если у RHF-поля есть `template` (даже
  // пустая строка означает «Switch включён, юзер ещё ничего не ввёл»).
  // useWatch для активного поля вернёт undefined после unregister'a.
  const isActiveEditorEnabled =
    activeTab === "title"
      ? titleTemplate !== undefined
      : descriptionTemplate !== undefined;

  const variablesState = useTemplateVariables();

  const previewProducts = useMemo(
    () => PREVIEW_SAMPLES.map(toPreviewProduct),
    [],
  );

  const previewState = useTemplatePreview({
    titleTemplate: titleTemplate?.template ? titleTemplate : undefined,
    descriptionTemplate: descriptionTemplate?.template
      ? descriptionTemplate
      : undefined,
    products: previewProducts,
    enabled: !!titleTemplate?.template || !!descriptionTemplate?.template,
  });

  const titleWarning = useMemo(() => {
    if (!titleTemplate?.template) return undefined;
    if (transformers.includes(TransformersEnum.GptTitle)) {
      return "Включён GPT-генератор названия. Превью применит шаблон к исходному title — в финальном фиде шаблон выполнится поверх GPT-результата.";
    }
    return undefined;
  }, [titleTemplate?.template, transformers]);

  const descriptionWarning = useMemo(() => {
    if (!descriptionTemplate?.template) return undefined;
    if (transformers.includes(TransformersEnum.GptDescription)) {
      return "Включён GPT-генератор описания. Превью показывает шаблон над исходным описанием — в финале он выполнится поверх GPT-результата.";
    }
    return undefined;
  }, [descriptionTemplate?.template, transformers]);

  return (
    <div className={css.wrapper}>
      <Alert
        theme="info"
        view="outlined"
        message="Шаблоны переписывают title/description товаров в момент экспорта. Используйте {{переменные}} из сайдбара справа — клик подставляет в курсор."
      />

      <div className={css.layout}>
        <div className={css.editors}>
          <TabProvider
            value={activeTab}
            onUpdate={(next) => {
              if (isTemplateField(next)) setActiveTab(next);
            }}
          >
            <TabList size="m" className={css.tabs}>
              <Tab value="title">{TAB_LABELS.title}</Tab>
              <Tab value="description">{TAB_LABELS.description}</Tab>
            </TabList>

            <TabPanel value="title">
              <Flex direction="column" gap={3} className={css.tabPanel}>
                <TemplateEditor
                  field="title"
                  title="Шаблон названия"
                  description="Собираем составной title из бренда, серии, исходного названия и т.д."
                  applyOnlyIfEmptyLabel="Применять только к товарам с пустым названием"
                  defaultMaxLength={undefined}
                  variables={variablesState.data}
                  previewData={previewState.data}
                  previewLoading={previewState.isLoading}
                  previewError={previewState.error}
                  previewProducts={PREVIEW_SAMPLES}
                  warning={titleWarning}
                  exposeInsert={exposeInsertForTitle}
                />
              </Flex>
            </TabPanel>

            <TabPanel value="description">
              <Flex direction="column" gap={3} className={css.tabPanel}>
                <TemplateEditor
                  field="description"
                  title="Шаблон описания"
                  description="Главный кейс — закрыть дыры у товаров без описания через шаблон + fallback."
                  applyOnlyIfEmptyLabel="Применять только к товарам с пустым описанием (рекомендуется)"
                  defaultMaxLength={undefined}
                  variables={variablesState.data}
                  previewData={previewState.data}
                  previewLoading={previewState.isLoading}
                  previewError={previewState.error}
                  previewProducts={PREVIEW_SAMPLES}
                  warning={descriptionWarning}
                  exposeInsert={exposeInsertForDescription}
                  richText={richDescription}
                />
              </Flex>
            </TabPanel>
          </TabProvider>
        </div>

        <aside className={css.sidebar}>
          <VariableSidebar
            data={variablesState.data}
            isLoading={variablesState.isLoading}
            error={variablesState.error}
            onInsert={handleSidebarInsert}
            disabled={!isActiveEditorEnabled}
            disabledHint={`Включите шаблон ${
              activeTab === "title" ? "названия" : "описания"
            }, чтобы вставлять переменные.`}
          />
        </aside>
      </div>
    </div>
  );
};
